import {useCallback} from 'react';
import get from 'lodash/get';
import type {GetValueById, UseValueById} from 'schema/form/types';
import type {DataValue} from 'schemaDefinition/types';
import type {RootState} from 'store/types';
import {useAppSelector, useAppStore} from 'store/hooks';

const getValue = (
  state: RootState,
  localPath: string | undefined,
  entityId: string,
): DataValue => {
  if (localPath === undefined) {
    return null;
  }

  const expression = (state.metadataEdit.metadata?.expressions ?? []).find(
    m => m.id === entityId,
  );
  return expression
    ? localPath === ''
      ? expression
      : (get(expression, localPath) ?? null)
    : null;
};

const useValueSelector = (localPath: string | undefined, entityId: string) =>
  useCallback(
    (state: RootState): DataValue => getValue(state, localPath, entityId),
    [entityId, localPath],
  );

/**
 * Hook to look up entity property value by entityId and local path.
 * Use to trigger updates (re-render) when store changes
 */
export const useExpressionEditValue: UseValueById = (
  localPath: string | undefined,
  entityId: string,
): DataValue | undefined => {
  const selector = useValueSelector(localPath, entityId);
  const value = useAppSelector(selector);

  return value;
};

/**
 * Returns function to look up entity property value by entityId and local path.
 * Use to lookup value in store when needed
 */
export const useGetExpressionEditValue = (): GetValueById => {
  const {getState} = useAppStore();
  return useCallback(
    (localPath: string | undefined, entityId: string): DataValue | undefined =>
      getValue(getState(), localPath, entityId),
    [getState],
  );
};
